import React from 'react'
import { VictoryAxis, VictoryChart, VictoryBar, VictoryGroup } from 'victory'
import { colors } from 'ic-snacks'
import { injectIntl, type IntlShape } from 'react-intl'
import { type InjectedIntlProps, type DashMessages } from 'intl/intl.types'
import {
  type ChartCoordinates,
  type ChartSeries,
  SeriesTypes,
} from 'legacy/components/DataViz/dataViz.types'
import useResizeListener from 'legacy/common/react/hooks/useResizeListener'
import { getSeriesColor } from 'legacy/components/DataViz/Charts/utils'
import {
  baseHorizontalAxisStyle,
  chartContainerStyle,
  verticalAxisStyle,
} from './CommonChartStyles'
import LabelTooltip from './LabelTooltip'

const BAR_WIDTH = 46

const barStyleSecondary = {
  data: {
    fill: colors.GRAY_74,
    opacity: 0.8,
  },
  labels: {
    fill: getSeriesColor(SeriesTypes.Secondary),
    fontFamily: 'inherit',
  },
}

const barStylePrimary = {
  data: {
    fill: colors.GREEN_500,
    opacity: 0.8,
  },
  labels: {
    fill: getSeriesColor(SeriesTypes.Primary),
    fontFamily: 'inherit',
  },
}

interface Props extends InjectedIntlProps {
  data: ChartCoordinates[][]
  series: ChartSeries[]
  dataType: 'currency' | 'number' | 'percent'
}

const generateLabel = (intl: IntlShape, label: DashMessages, val: string): string => {
  const starRating = intl.formatMessage({ id: label })
  const percentageValue = intl.formatNumber(Number(val), {
    style: 'percent',
    minimumFractionDigits: 2,
  })

  return `${starRating} - ${percentageValue}`
}

const BarChart: React.FC<Props> = props => {
  const [setDimensionRef, { width }] = useResizeListener(200)
  const { intl } = props
  const data = props.data.map(dataSet => dataSet.map(({ x, y }) => ({ x, y: Number(y) })))

  return (
    <div ref={setDimensionRef}>
      {width && (
        <VictoryChart
          padding={{ top: 10, bottom: 40, left: 80, right: 10 }}
          width={width}
          height={300}
          domainPadding={{ x: 60, y: 30 }}
          style={chartContainerStyle}
        >
          <VictoryAxis
            tickFormat={data[0].map(({ x }) => intl.formatMessage({ id: x }))}
            style={baseHorizontalAxisStyle}
          />
          <VictoryAxis
            dependentAxis
            tickFormat={y => intl.formatNumber(y, { style: 'percent' })}
            style={verticalAxisStyle}
          />
          <VictoryGroup offset={BAR_WIDTH} labelComponent={<LabelTooltip />}>
            <VictoryBar
              barWidth={BAR_WIDTH}
              style={barStylePrimary}
              data={data[0]}
              labels={({ x, y }) => generateLabel(intl, x, y)}
            />
            <VictoryBar
              barWidth={BAR_WIDTH}
              style={barStyleSecondary}
              data={data[1]}
              labels={({ x, y }) => generateLabel(intl, x, y)}
            />
          </VictoryGroup>
        </VictoryChart>
      )}
    </div>
  )
}

export default injectIntl(BarChart)
